import { TUser, TUserRoles } from '@/features/users/schemas/users.schema.ts';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';

type PermissionCheck<Key extends keyof Permissions> =
  | boolean
  | ((user: Pick<TUser, 'userId' | 'role'>, data?: Permissions[Key]['dataType']) => boolean);

type RolesWithPermissions = {
  [R in TUserRoles]: Partial<{
    [Key in keyof Permissions]: Partial<{
      [Action in Permissions[Key]['action']]: PermissionCheck<Key>;
    }>;
  }>;
};

export type Permissions = {
  navigation: {
    dataType: undefined;
    action:
      | 'navigation.main-menu'
      | 'navigation.main-menu.dashboard'
      | 'navigation.main-menu.plants'
      | 'navigation.main-menu.bess'
      | 'navigation.main-menu.devices'
      | 'navigation.main-menu.release-notes'
      | 'navigation.plants'
      | 'navigation.tools'
      | 'navigation.tools.event-rules'
      | 'navigation.tools.notification-rules'
      | 'navigation.admin-menu'
      | 'navigation.admin-menu.users';
  };
  dashboard: {
    dataType: undefined;
    action: 'view-page.dashboard';
  };
  plants: {
    dataType: Pick<TPlant, 'plantId' | 'companyId'>;
    action:
      | 'view-page.plants'
      | 'view-page.plant'
      | 'view-page.control-panel'
      | 'view-page.schedule'
      | 'view-page.create'
      | 'view-page.edit'
      | 'view-page.connection-details'
      | 'view-page.user-access-management'
      | 'view-generate-report.plant'
      | 'edit.plant.production-fields'
      | 'edit.plant.storage-fields'
      | 'create'
      | 'update'
      | 'update.control-panel'
      | 'update.access-management'
      | 'delete';
  };
  bess: {
    dataType: undefined;
    action: 'view-page.bess';
  };
  devices: {
    dataType: undefined;
    action: 'view-page.devices' | 'view-page.device';
  };
  users: {
    dataType: Pick<TUser, 'userId' | 'role'>;
    action:
      | 'view-page.users'
      | 'view-page.user-access-management'
      | 'invite'
      | 'update'
      | 'update.access-management';
  };
  'event-rules': {
    dataType: undefined;
    action: 'view-page.event-rules' | 'create' | 'update' | 'delete';
  };
  'notification-rules': {
    dataType: undefined;
    action:
      | 'view-page.notification-rules'
      | 'create'
      | 'update'
      | 'delete'
      | 'update.access-management';
  };
  'my-profile': {
    dataType: undefined;
    action: 'view-page.my-profile';
  };
  notifications: {
    dataType: undefined;
    action: 'view-page.notifications';
  };
  'date-range-picker': {
    dataType: undefined;
    action:
      | 'view-granularity.1min'
      | 'view-granularity.5min'
      | 'view-granularity.15min'
      | 'view-granularity.hourly'
      | 'view-granularity.daily'
      | 'view-granularity.monthly';
  };
  'energy-management-chart': {
    dataType: undefined;
    action:
      | 'view-metric.pv_production'
      | 'view-metric.consumed'
      | 'view-metric.consumed_from_grid'
      | 'view-metric.fed_to_grid'
      | 'view-metric.battery_charge'
      | 'view-metric.battery_discharge'
      | 'view-metric.battery_state_of_charge'
      | 'view-metric.ropex_dam_price';
  };
};

export type Resource = keyof Permissions;

//
// //
//

const ROLES = {
  'Company owner': {
    navigation: {
      'navigation.main-menu': true,
      'navigation.main-menu.dashboard': true,
      'navigation.main-menu.plants': true,
      'navigation.main-menu.bess': true,
      'navigation.main-menu.devices': true,
      'navigation.main-menu.release-notes': true,
      'navigation.tools': true,
      'navigation.tools.event-rules': true,
      'navigation.tools.notification-rules': true,
      'navigation.admin-menu': true,
      'navigation.admin-menu.users': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-page.plants': true,
      'view-page.plant': true,
      'view-page.control-panel': true,
      'view-generate-report.plant': true,
      'view-page.schedule': true,
      'view-page.create': true,
      'view-page.edit': true,
      'view-page.connection-details': true,
      'view-page.user-access-management': true,
      'edit.plant.production-fields': true,
      'edit.plant.storage-fields': true,
      create: true,
      update: true,
      'update.control-panel': true,
      'update.access-management': true,
      delete: true,
    },
    bess: {
      'view-page.bess': true,
    },
    devices: {
      'view-page.devices': true,
      'view-page.device': true,
    },
    users: {
      'view-page.users': true,
      'view-page.user-access-management': true,
      invite: true,
      update: true,
      'update.access-management': true,
    },
    'event-rules': {
      'view-page.event-rules': true,
      create: true,
      update: true,
      delete: true,
    },
    'notification-rules': {
      'view-page.notification-rules': true,
      create: true,
      update: true,
      delete: true,
      'update.access-management': true,
    },
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
    'date-range-picker': {
      'view-granularity.1min': true,
      'view-granularity.5min': true,
      'view-granularity.15min': true,
      'view-granularity.hourly': true,
      'view-granularity.daily': true,
      'view-granularity.monthly': true,
    },
    'energy-management-chart': {
      'view-metric.pv_production': true,
      'view-metric.consumed': true,
      'view-metric.consumed_from_grid': true,
      'view-metric.fed_to_grid': true,
      'view-metric.battery_charge': true,
      'view-metric.battery_discharge': true,
      'view-metric.battery_state_of_charge': true,
      'view-metric.ropex_dam_price': true,
    },
  },

  'Company administrator': {
    navigation: {
      'navigation.main-menu': true,
      'navigation.main-menu.dashboard': true,
      'navigation.main-menu.plants': true,
      'navigation.main-menu.bess': true,
      'navigation.main-menu.devices': true,
      'navigation.main-menu.release-notes': true,
      'navigation.tools': true,
      'navigation.tools.event-rules': true,
      'navigation.tools.notification-rules': true,
      'navigation.admin-menu': true,
      'navigation.admin-menu.users': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-page.plants': true,
      'view-page.plant': true,
      'view-page.control-panel': true,
      'view-generate-report.plant': true,
      'view-page.schedule': true,
      'view-page.create': true,
      'view-page.edit': true,
      'view-page.connection-details': true,
      'view-page.user-access-management': true,
      'edit.plant.production-fields': true,
      'edit.plant.storage-fields': true,
      create: true,
      update: true,
      'update.control-panel': true,
      'update.access-management': true,
      delete: true,
    },
    bess: {
      'view-page.bess': true,
    },
    devices: {
      'view-page.devices': true,
      'view-page.device': true,
    },
    users: {
      'view-page.users': true,
      'view-page.user-access-management': true,
      invite: true,
      update: true,
      'update.access-management': true,
    },
    'event-rules': {
      'view-page.event-rules': true,
      create: true,
      update: true,
      delete: true,
    },
    'notification-rules': {
      'view-page.notification-rules': true,
      create: true,
      update: true,
      delete: true,
      'update.access-management': true,
    },
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
    'date-range-picker': {
      'view-granularity.1min': true,
      'view-granularity.5min': true,
      'view-granularity.15min': true,
      'view-granularity.hourly': true,
      'view-granularity.daily': true,
      'view-granularity.monthly': true,
    },
    'energy-management-chart': {
      'view-metric.pv_production': true,
      'view-metric.consumed': true,
      'view-metric.consumed_from_grid': true,
      'view-metric.fed_to_grid': true,
      'view-metric.battery_charge': true,
      'view-metric.battery_discharge': true,
      'view-metric.battery_state_of_charge': true,
      'view-metric.ropex_dam_price': true,
    },
  },

  Installer: {
    navigation: {
      'navigation.main-menu': true,
      'navigation.main-menu.dashboard': true,
      'navigation.main-menu.plants': true,
      'navigation.main-menu.bess': true,
      'navigation.main-menu.devices': true,
      'navigation.main-menu.release-notes': true,
      'navigation.tools': true,
      'navigation.tools.notification-rules': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-page.plants': true,
      'view-page.plant': true,
      'view-page.control-panel': true,
      'view-generate-report.plant': true,
      'view-page.schedule': true,
      'view-page.create': true,
      'view-page.edit': true,
      'view-page.connection-details': true,
    },
    bess: {
      'view-page.bess': true,
    },
    devices: {
      'view-page.devices': true,
      'view-page.device': true,
    },
    'notification-rules': {
      'view-page.notification-rules': true,
      create: true,
      update: true,
      delete: true,
    },
    users: {
      invite: true,
    },
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
    'date-range-picker': {
      'view-granularity.1min': true,
      'view-granularity.5min': true,
      'view-granularity.15min': true,
      'view-granularity.hourly': true,
      'view-granularity.daily': true,
      'view-granularity.monthly': true,
    },
    'energy-management-chart': {
      'view-metric.pv_production': true,
      'view-metric.consumed': true,
      'view-metric.consumed_from_grid': true,
      'view-metric.fed_to_grid': true,
      'view-metric.battery_charge': true,
      'view-metric.battery_discharge': true,
      'view-metric.battery_state_of_charge': true,
      'view-metric.ropex_dam_price': true,
    },
  },

  Manufacturer: {
    navigation: {
      'navigation.plants': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {},
    devices: {},
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
    'date-range-picker': {
      'view-granularity.5min': true,
      'view-granularity.hourly': true,
      'view-granularity.daily': true,
      'view-granularity.monthly': true,
    },
    'energy-management-chart': {
      'view-metric.pv_production': true,
      'view-metric.consumed': true,
      'view-metric.consumed_from_grid': true,
      'view-metric.fed_to_grid': true,
      'view-metric.battery_charge': true,
      'view-metric.battery_discharge': true,
      'view-metric.battery_state_of_charge': true,
      'view-metric.ropex_dam_price': true,
    },
  },

  Owner: {
    navigation: {
      'navigation.plants': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-generate-report.plant': true,
    },
    devices: {},
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
    'date-range-picker': {
      'view-granularity.5min': true,
      'view-granularity.hourly': true,
      'view-granularity.daily': true,
      'view-granularity.monthly': true,
    },
    'energy-management-chart': {
      'view-metric.pv_production': true,
      'view-metric.consumed': true,
      'view-metric.consumed_from_grid': true,
      'view-metric.fed_to_grid': true,
      'view-metric.battery_charge': true,
      'view-metric.battery_discharge': true,
      'view-metric.battery_state_of_charge': true,
      'view-metric.ropex_dam_price': true,
    },
  },

  Guest: {
    navigation: {
      'navigation.plants': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-generate-report.plant': true,
    },
    devices: {},
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
    'date-range-picker': {
      'view-granularity.5min': true,
      'view-granularity.hourly': true,
      'view-granularity.daily': true,
      'view-granularity.monthly': true,
    },
    'energy-management-chart': {
      'view-metric.pv_production': true,
      'view-metric.consumed': true,
      'view-metric.consumed_from_grid': true,
      'view-metric.fed_to_grid': true,
      'view-metric.battery_charge': true,
      'view-metric.battery_discharge': true,
      'view-metric.battery_state_of_charge': true,
      'view-metric.ropex_dam_price': true,
    },
  },

  Unknown: {
    navigation: {},
    dashboard: {},
    plants: {},
    devices: {},
    users: {},
    'my-profile': {},
    notifications: {},
    'date-range-picker': {},
    'energy-management-chart': {},
  },
} as const satisfies RolesWithPermissions;

export function hasPermission<Resource extends keyof Permissions>(
  user: Pick<TUser, 'userId' | 'role'>,
  resource: Resource,
  action: Permissions[Resource]['action'],
  data?: Permissions[Resource]['dataType']
) {
  const permission = (ROLES as RolesWithPermissions)[user.role][resource]?.[action];
  if (permission == null) return false;

  if (typeof permission === 'boolean') return permission;
  return data != null && permission(user, data);
}

export function hasPermissions<Resource extends keyof Permissions>(
  user: Pick<TUser, 'userId' | 'role'>,
  checks: Array<
    | [Resource, Permissions[Resource]['action']]
    | [Resource, Permissions[Resource]['action'], Permissions[Resource]['dataType']]
  >
): boolean[] {
  return checks.map(([resource, action, data]) => hasPermission(user, resource, action, data));
}
